import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
    Drawer,
    Button,
    Form,
    Input,
    message,
    Icon,
    Tooltip,
    Divider,
} from "antd";
import styled from "styled-components";

import InputCurrency from "~/components/UI/InputCurrency";
import * as bancosActions from "../actions";

var TextArea = Input.TextArea;

var DrawerFooter = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 24px;
    background: #fff;
    border-top: 1px solid #e8e8e8;
    display: flex;
    gap: 12px;
`;

var SectionLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.45);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 12px;
`;

var ExtraFieldsToggle = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: #1890ff;
    font-size: 13px;
    margin-bottom: 16px;
    user-select: none;

    &:hover {
        color: #40a9ff;
    }
`;

var DrawerCreate = function(props) {
    var visible = props.visible;
    var onClose = props.onClose;
    var onSuccess = props.onSuccess;
    var record = props.record;

    var dispatch = useDispatch();
    var [isSubmitting, setIsSubmitting] = useState(false);
    var [showExtraFields, setShowExtraFields] = useState(false);
    var [saldoNegative, setSaldoNegative] = useState(false);
    var [formData, setFormData] = useState({
        name: '',
        bank_code: '',
        agency: '',
        account_number: '',
        saldo_inicial: 0,
        description: '',
    });

    useEffect(function() {
        if (visible) {
            if (record) {
                var hasExtra = !!(record.bank_code || record.agency || record.account_number);
                setShowExtraFields(hasExtra);
                setSaldoNegative((record.saldo_inicial || 0) < 0);
                setFormData({
                    name: record.name || '',
                    bank_code: record.bank_code || '',
                    agency: record.agency || '',
                    account_number: record.account_number || '',
                    saldo_inicial: Math.abs(record.saldo_inicial || 0),
                    description: record.description || '',
                });
            } else {
                setShowExtraFields(false);
                setSaldoNegative(false);
                setFormData({
                    name: '',
                    bank_code: '',
                    agency: '',
                    account_number: '',
                    saldo_inicial: 0,
                    description: '',
                });
            }
        }
    }, [visible, record]);

    var handleSubmit = function() {
        if (!formData.name || !formData.name.trim()) {
            message.error('Informe o nome do banco');
            return;
        }

        setIsSubmitting(true);

        var data = {
            name: formData.name,
            bank_code: formData.bank_code || null,
            agency: formData.agency || null,
            account_number: formData.account_number || null,
            saldo_inicial: saldoNegative ? -(formData.saldo_inicial || 0) : (formData.saldo_inicial || 0),
            description: formData.description || null,
        };

        var action;
        if (record) {
            action = dispatch(bancosActions.updateBanco(record.id, data));
        } else {
            action = dispatch(bancosActions.createBanco(data));
        }

        action.then(function(response) {
            setIsSubmitting(false);
            if (response && response.error) {
                var errMsg = (response.error.response && response.error.response.data && response.error.response.data.message)
                    || 'Erro ao salvar banco';
                message.error(errMsg);
            } else {
                message.success(record ? 'Banco atualizado com sucesso' : 'Banco criado com sucesso');
                if (onSuccess) onSuccess();
            }
        });
    };

    var handleFieldChange = function(field, value) {
        setFormData(function(prev) {
            var next = {};
            Object.keys(prev).forEach(function(k) { next[k] = prev[k]; });
            next[field] = value;
            return next;
        });
    };

    var handleToggleExtra = function() {
        setShowExtraFields(function(prev) { return !prev; });
    };

    return (
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon type="bank" style={{ color: '#1890ff', fontSize: 18 }} />
                    <span style={{ fontWeight: 600 }}>{record ? 'Editar Banco' : 'Novo Banco'}</span>
                </div>
            }
            placement="right"
            visible={visible}
            onClose={onClose}
            width={500}
            bodyStyle={{ paddingBottom: 80 }}
        >
            <Form layout="vertical">
                <SectionLabel>Identificação</SectionLabel>

                <Form.Item label="Nome do Banco" required>
                    <Input
                        placeholder="Ex: Bradesco, Itaú, Nubank..."
                        value={formData.name}
                        size="large"
                        onChange={function(e) { handleFieldChange('name', e.target.value); }}
                    />
                </Form.Item>

                <Divider style={{ margin: '8px 0 20px' }} />
                <SectionLabel>Saldo</SectionLabel>

                <Form.Item
                    label="Saldo Inicial"
                    extra={record ? 'O saldo inicial não pode ser alterado após o cadastro.' : 'Valor do saldo no momento do cadastro do banco.'}
                >
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Tooltip title={record ? 'Não editável' : saldoNegative ? 'Clique para tornar positivo' : 'Clique para tornar negativo'}>
                            <Button
                                size="large"
                                disabled={!!record}
                                style={{
                                    width: 48,
                                    fontWeight: 700,
                                    fontSize: 18,
                                    color: saldoNegative ? '#f5222d' : '#52c41a',
                                    borderColor: saldoNegative ? '#f5222d' : '#52c41a',
                                    background: saldoNegative ? '#fff1f0' : '#f6ffed',
                                }}
                                onClick={function() { setSaldoNegative(function(prev) { return !prev; }); }}
                            >
                                {saldoNegative ? '-' : '+'}
                            </Button>
                        </Tooltip>
                        <InputCurrency
                            value={formData.saldo_inicial}
                            disabled={!!record}
                            style={{
                                flex: 1,
                                height: 40,
                                fontSize: 16,
                                fontWeight: 600,
                                color: saldoNegative ? '#f5222d' : formData.saldo_inicial > 0 ? '#52c41a' : 'inherit',
                            }}
                            onChangeEvent={function(e, maskedValue, floatValue) { handleFieldChange('saldo_inicial', floatValue); }}
                        />
                    </div>
                </Form.Item>

                <Divider style={{ margin: '8px 0 20px' }} />
                <SectionLabel>Observações</SectionLabel>

                <Form.Item label="Descrição">
                    <TextArea
                        rows={3}
                        placeholder="Observações sobre esta conta bancária..."
                        value={formData.description}
                        onChange={function(e) { handleFieldChange('description', e.target.value); }}
                    />
                </Form.Item>

                <ExtraFieldsToggle onClick={handleToggleExtra}>
                    <Icon type={showExtraFields ? 'up-circle' : 'down-circle'} />
                    {showExtraFields ? 'Ocultar informações bancárias' : 'Adicionar código, agência e conta'}
                </ExtraFieldsToggle>

                {showExtraFields && (
                    <div>
                        <Form.Item label="Código do Banco">
                            <Input
                                placeholder="Ex: 237, 341, 033..."
                                value={formData.bank_code}
                                onChange={function(e) { handleFieldChange('bank_code', e.target.value); }}
                            />
                        </Form.Item>

                        <div style={{ display: 'flex', gap: 12 }}>
                            <Form.Item label="Agência" style={{ flex: 1 }}>
                                <Input
                                    placeholder="Ex: 0001-9"
                                    value={formData.agency}
                                    onChange={function(e) { handleFieldChange('agency', e.target.value); }}
                                />
                            </Form.Item>

                            <Form.Item label="Conta" style={{ flex: 1 }}>
                                <Input
                                    placeholder="Ex: 12345-6"
                                    value={formData.account_number}
                                    onChange={function(e) { handleFieldChange('account_number', e.target.value); }}
                                />
                            </Form.Item>
                        </div>
                    </div>
                )}
            </Form>

            <DrawerFooter>
                <Button onClick={onClose} size="large" style={{ flex: 1 }} disabled={isSubmitting}>
                    Cancelar
                </Button>
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    size="large"
                    style={{ flex: 2 }}
                >
                    <Icon type="check" /> {record ? 'Salvar alterações' : 'Criar Banco'}
                </Button>
            </DrawerFooter>
        </Drawer>
    );
};

export default DrawerCreate;
